<template>
  <div class="chart-container">
    <!-- Показатели и график -->
    <div class="chart-row">
      <!-- Показатели -->
      <!--       <div class="vertical-label">
        <span>ммоль/л</span>
      </div> -->

      <!-- Прокручиваемый график -->
      <div class="chart-wrapper">
        <div class="scrollable-chart-container">
          <Chart
            type="line"
            :data="chartData"
            :options="chartOptions"
            height="200px"
            class="chart"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    class="ps-2 pe-2 pe-0 py-2 mt-2 blocktext"
    style="border-left: 10px solid #4caf50"
  >
    За последнюю неделю вы достигли впечатляющих результатов, масса тела
    уменьшилась на 10%. Продолжайте соблюдать рекомендации по питанию и высокий
    уровень физической активности.
  </div>
  <div class="pt-4">
    <DataTable
      :value="analyses"
      tableStyle="min-width: 100%"
      class="analysis-table"
      :showHeader="false"
      :showGridlines="false"
    >
      <!-- Первая колонка: Дата и показатели -->
      <Column header="История показателей">
        <template #body="{ data }">
          <div class="analysis-info">
            <!-- Дата анализа -->
            <div class="analysis-date">
              {{ data.date }}
              <span
                :style="{
                  color: data.ves > data.ves_b ? '#dc3545' : '#20c997',
                }"
              >
                {{
                  data.ves_b
                    ? data.ves > data.ves_b
                      ? " &#9650;"
                      : data.ves_b == data.ves
                      ? ""
                      : " &#9660;"
                    : ""
                }}
              </span>
            </div>
            <!-- Показатели -->
            <div class="analysis-details">
              <span>Масса тела: {{ data.ves }}</span>
              <span>ИМТ: {{ data.imt }}</span>
            </div>
          </div>
        </template>
      </Column>

      <!-- Вторая колонка: Иконка -->
      <Column class="w-24 !text-end">
        <template #body="">
          <div class="expand-icon" @click="viewresult = true">
            <i class="pi pi-chevron-right"></i>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
  <!-- Плавающая кнопка -->
  <button class="floating-button" @click="visibleBottom = true">
    <i class="pi pi-plus"></i>
  </button>

  <Drawer
    v-model:visible="visibleBottom"
    header="Добавить измерение"
    position="bottom"
    class="drawer-weight"
  >
    <WeightPicker
      :initialWeight="analyses[0]?.ves || getLastWeightFromStorage() || 70"
      :maxWeight="200"
      :step="0.1"
      :viewportWidth="250"
      v-model="selectedWeight"
      @save="onWeightChange"
    />
  </Drawer>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import WeightPicker from "./WeightPicker.vue";
import Chart from "primevue/chart";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Drawer from "primevue/drawer";
import { fetchWeight, addWeight } from "@/services/api";
import { useToast } from "primevue/usetoast";

export default defineComponent({
  components: {
    WeightPicker,
    Chart,
    DataTable,
    Column,
    Drawer,
  },
  setup() {
    const toast = useToast();
    const selectedWeight = ref(70);
    const visibleBottom = ref(false);
    const isLoading = ref(false);
    const analyses = ref<any[]>([]);

    // Форматирование даты для отображения
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    // Подготовка данных для графика
    const chartData = computed(() => {
      const sortedData = [...analyses.value].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      return {
        labels: sortedData.map((item) => formatDate(item.created_at)),
        datasets: [
          {
            label: "Масса тела",
            data: sortedData.map((item) => item.current_weight),
            borderColor: "#4caf50",
            backgroundColor: "rgba(76, 175, 80, 0.2)",
            tension: 0.4,
          },
        ],
      };
    });

    const chartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "",
          },
        },
        y: {
          title: {
            display: true,
            text: "кг",
          },
        },
      },
    });

    // Загрузка данных
    const loadWeightData = async () => {
      try {
        isLoading.value = true;
        const data = await fetchWeight();
        if (data) {
          // Сначала сортируем данные
          const sortedData = data.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );

          // Затем маппим с правильным предыдущим значением
          analyses.value = sortedData.map((item: any, index: number) => ({
            ...item,
            date: formatDate(item.created_at),
            ves: item.current_weight,
            imt: item.bmi,
            ves_b:
              index < sortedData.length - 1
                ? sortedData[index + 1].current_weight
                : null,
          }));
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных веса:", error);
        toast.add({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить данные веса",
          life: 3000,
        });
      } finally {
        isLoading.value = false;
      }
    };

    // Добавление нового измерения
    const onWeightChange = async (value: number) => {
      try {
        selectedWeight.value = value;
        // Рассчитываем ИМТ (нужно получить рост из профиля пользователя)
        const height = 1.75; // Здесь нужно получить реальный рост пользователя
        const bmi = Number((value / (height * height)).toFixed(1));

        await addWeight({
          current_weight: value,
          bmi: bmi,
        });

        // Перезагружаем данные
        await loadWeightData();

        visibleBottom.value = false;

        toast.add({
          severity: "success",
          summary: "Успешно",
          detail: "Измерение веса добавлено",
          life: 3000,
        });
      } catch (error) {
        console.error("Ошибка при добавлении измерения:", error);
        toast.add({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось добавить измерение",
          life: 3000,
        });
      }
    };

    const getLastWeightFromStorage = () => {
      const savedWeight = localStorage.getItem("lastWeight");
      return savedWeight ? parseFloat(savedWeight) : null;
    };

    onMounted(() => {
      loadWeightData();
    });

    return {
      selectedWeight,
      onWeightChange,
      chartData,
      chartOptions,
      analyses,
      visibleBottom,
      isLoading,
      getLastWeightFromStorage,
    };
  },
});
</script>

<style lang="scss">
.drawer-weight {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-color: #f8f9fa !important;
  border: 0;
  height: auto !important;
}
</style>
